import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, TrendingUp, ExternalLink } from 'lucide-react';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MONTHS_PT = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

export default function AdminCardPayments() {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { navigate('/backoffice'); return; }
    axios.get(`${API_URL}/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setOrders(res.data.data))
      .catch(e => { if (e.response?.status === 401) { localStorage.removeItem('adminToken'); navigate('/backoffice'); } })
      .finally(() => setLoading(false));
  }, []);

  const { cardOrders, monthlyData, totals } = useMemo(() => {
    const cardOrders = orders
      .filter(o => o.paymentMethod === 'card' && o.paymentStatus === 'paid')
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // Agrupar por mês
    const monthMap = {};
    cardOrders.forEach(o => {
      const d = new Date(o.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthMap[key]) monthMap[key] = { year: d.getFullYear(), month: d.getMonth(), revenue: 0, count: 0 };
      monthMap[key].revenue += parseFloat(o.price);
      monthMap[key].count++;
    });

    const monthlyData = Object.entries(monthMap)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([, v]) => v);

    const totals = {
      revenue: cardOrders.reduce((s, o) => s + parseFloat(o.price), 0),
      count: cardOrders.length,
    };

    return { cardOrders, monthlyData, totals };
  }, [orders]);

  const getServiceLabel = (o) => {
    if (o.items && Array.isArray(o.items) && o.items.length > 0) {
      return o.items.map(i => `${i.quantity?.toLocaleString('pt-PT')} ${i.serviceType === 'likes' ? 'Likes' : i.serviceType === 'views' ? 'Views' : 'Seg.'} ${i.platform}`).join(', ');
    }
    return `${o.followersCount?.toLocaleString('pt-PT') || '—'} ${o.serviceType === 'likes' ? 'Likes' : o.serviceType === 'views' ? 'Views' : 'Seg.'} ${o.platform || ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">A carregar...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader activePage="card" />

      <div className="container mx-auto px-4 py-6 space-y-6">

        {/* Totais */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 bg-purple-500 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Receita Total Cartão</p>
            </div>
            <p className="text-3xl font-extrabold text-gray-900">€{totals.revenue.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 bg-pink-500 rounded-xl flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pagamentos por Cartão</p>
            </div>
            <p className="text-3xl font-extrabold text-gray-900">{totals.count}</p>
          </div>
        </div>

        {/* Resumo por mês */}
        {monthlyData.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <p className="font-bold text-gray-900">Resumo por Mês</p>
            </div>
            <div className="divide-y divide-gray-50">
              {monthlyData.map((m, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-50 rounded-lg flex flex-col items-center justify-center">
                      <span className="text-xs font-bold text-purple-600">{MONTHS_PT[m.month]}</span>
                      <span className="text-xs text-purple-400">{m.year}</span>
                    </div>
                    <p className="text-sm text-gray-500">{m.count} pagamento{m.count !== 1 ? 's' : ''}</p>
                  </div>
                  <p className="text-xl font-extrabold text-gray-900">€{m.revenue.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lista de transações */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <p className="font-bold text-gray-900">Todas as Transações</p>
            <p className="text-xs text-gray-400 mt-0.5">{cardOrders.length} pagamento{cardOrders.length !== 1 ? 's' : ''} por cartão</p>
          </div>

          {cardOrders.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <CreditCard className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400">Ainda não há pagamentos por cartão</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {cardOrders.map((o, i) => {
                const d = new Date(o.createdAt);
                return (
                  <div key={i} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-purple-700">{String(d.getDate()).padStart(2,'0')}</span>
                      <span className="text-xs text-purple-500">{MONTHS_PT[d.getMonth()]}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">{o.customerName}</p>
                        <button
                          onClick={() => navigate(`/backoffice/order/${o.orderCode}`)}
                          className="text-purple-500 hover:text-purple-700 transition flex-shrink-0"
                          title="Ver pedido"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 truncate">{o.orderCode} · {getServiceLabel(o)}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-extrabold text-gray-900">€{parseFloat(o.price).toFixed(2)}</p>
                      <p className="text-xs text-gray-400">{d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
